import { Button } from '@/components/ui/button';
import { ScheduledServiceField } from '@/components/cashier-pos/ScheduledServiceField';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  ShoppingCart, X, Trash2, Plus, Minus, Save, Banknote, CreditCard, Users,
  User, ChefHat, Coffee, Printer, Percent, DollarSign, Building2, Leaf, UtensilsCrossed,
} from 'lucide-react';
import POSUpsellStrip from '@/components/pos/POSUpsellStrip';
import { useLanguage } from '@/contexts/LanguageContext';
import { resolveItemRoute } from '@/lib/printRouting';

// Right-side Cart panel: header (order type / table #), cart items list,
// upsell strip, totals, discount input, and payment buttons.
// Pure UI — all state + handlers are passed in as props from CashierPOSPage.
export const PosCart = ({
  // Layout
  showMobilePosCart,
  setShowMobilePosCart,
  // Cart & order
  cart,
  editingOrder,
  onCancelEdit,
  orderType,
  setOrderType,
  goGreen,
  onToggleGoGreen,
  onStaffFood,
  tableNumber,
  setTableNumber,
  scheduledFor,
  setScheduledFor,
  // Line-item actions
  updateQuantity,
  removeFromCart,
  // Totals
  subtotal,
  discountAmount,
  tax,
  total,
  currencySymbol,
  branchTax,
  // Discount
  discount,
  setDiscount,
  discountType,
  setDiscountType,
  // Payment
  paymentMethod,
  setPaymentMethod,
  setShowPayment,
  setShowCustomerSelect,
  selectedCustomer,
  setSelectedCustomer,
  onSaveEdit,
  onHoldBill,
  // Upsells
  upsellSuggestions,
  menuItems,
  handleAddItem,
  // Routing chip context
  categories,
  printers,
  user,
  branchName,
}) => {
  const { t, isRTL } = useLanguage();
  // VAT-inclusive display: exclusive items get VAT added on top for display only.
  const cartTaxRate = (branchTax?.tax_rate || 0) / 100;
  const grossOf = (n, inc) => (inc ? Number(n || 0) : Number(n || 0) * (1 + cartTaxRate));
  return (
  <div className={`${showMobilePosCart ? 'fixed inset-0 z-50 bg-black/50 lg:relative lg:inset-auto lg:bg-transparent' : 'hidden lg:flex'} lg:w-[32%] lg:min-w-[300px] lg:max-w-[400px] shrink-0`}>
    <div className={`${showMobilePosCart ? `absolute ${isRTL ? 'left-0' : 'right-0'} top-0 bottom-0 w-[85vw] max-w-96` : 'w-full'} bg-white shadow-lg flex flex-col h-full`}>
      {/* Cart Header */}
      <div className="p-4 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
            <ShoppingCart size={16} className="text-primary" />
          </div>
          <h2 className="font-bold text-sm">
            {editingOrder ? `${t('pos_editing')} #${editingOrder.order_number}` : t('pos_current_order')}
          </h2>
          {branchName && (
            <span
              data-testid="pos-cart-branch-badge"
              className="inline-flex items-center gap-1 rounded-full bg-primary/10 text-primary border border-primary/20 px-2 py-0.5 text-[10px] font-semibold"
              title="This till is acting as this branch"
            >
              <Building2 size={11} /> {branchName}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {editingOrder && (
            <Button size="sm" variant="ghost" className="h-7 text-xs text-muted-foreground rounded-lg" onClick={onCancelEdit}>{t('cancel')}</Button>
          )}
          <Button size="sm" variant="ghost" className="lg:hidden h-10 w-10 p-0 rounded-lg" onClick={() => setShowMobilePosCart(false)}>
            <X size={16} />
          </Button>
        </div>
      </div>

      <div className="px-4 pb-3 flex gap-2 items-center">
        <div className="flex gap-2">
          <Select value={orderType} onValueChange={setOrderType}>
            <SelectTrigger className="w-28 h-10 text-xs rounded-lg border-border">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="dine_in">{t('pos_dine_in')}</SelectItem>
              <SelectItem value="takeaway">{t('pos_takeaway')}</SelectItem>
              <SelectItem value="delivery">{t('pos_delivery')}</SelectItem>
            </SelectContent>
          </Select>
          {orderType === 'dine_in' && (
            <Input
              placeholder={t('pos_table_no')}
              value={tableNumber}
              onChange={(e) => setTableNumber(e.target.value)}
              className="w-20 h-10 text-xs rounded-lg border-border"
            />
          )}
        </div>
        {onToggleGoGreen && (
          <button
            type="button"
            onClick={onToggleGoGreen}
            data-testid="pos-cart-go-green"
            title="Go Green — skip printing the receipt for this order"
            className={`ms-auto inline-flex items-center gap-1 h-10 px-2.5 rounded-lg text-xs font-semibold border transition-colors ${goGreen ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-emerald-700 border-emerald-300 hover:bg-emerald-50'}`}
          >
            <Leaf size={13} /> {goGreen ? 'Going Green' : 'Go Green'}
          </button>
        )}
        {onStaffFood && (
          <button
            type="button"
            onClick={onStaffFood}
            data-testid="pos-cart-staff-food"
            title="Record these items as free staff food (SAR 0, deducts stock)"
            className={`inline-flex items-center gap-1 h-10 px-2.5 rounded-lg text-xs font-semibold border border-amber-300 bg-white text-amber-700 hover:bg-amber-50 transition-colors ${onToggleGoGreen ? '' : 'ms-auto'}`}
          >
            <UtensilsCrossed size={13} /> Staff Food
          </button>
        )}
      </div>
      {setScheduledFor && (
        <div className="px-4 pb-3 -mt-1"><ScheduledServiceField value={scheduledFor || ''} onChange={setScheduledFor} /></div>
      )}

      {/* Cart Items - cleaner cards */}
      <div className="flex-1 overflow-y-auto px-4 space-y-2 border-t border-border pt-3">
        {cart.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            <ShoppingCart size={40} className="mx-auto mb-3" />
            <p className="text-sm font-medium text-muted-foreground">{t('pos_cart_empty')}</p>
            <p className="text-xs text-muted-foreground">{t('pos_tap_items')}</p>
          </div>
        ) : (
          cart.map((item, index) => {
            const route = resolveItemRoute({
              categoryId: item.category,
              categories,
              printers,
              branchId: user?.branch_id,
            });
            return (
              <div key={index} className="bg-muted/50 rounded-xl p-3" data-testid={`cart-item-${index}`}>
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h4 className="font-semibold text-[13px] text-foreground">{item.name}</h4>
                    {item.modifiers?.length > 0 && (
                      <div className="text-[11px] text-muted-foreground mt-0.5">
                        {item.modifiers.map((m, i) => (
                          <span key={i} className="mr-1.5">+{m.name}</span>
                        ))}
                      </div>
                    )}
                    <p className="text-[11px] text-primary mt-0.5">{currencySymbol} {grossOf(item.unit_price + item.modifier_total, item.price_includes_tax).toFixed(2)} each</p>
                    {route.role !== 'none' && (
                      <div
                        className={`inline-flex items-center gap-1 mt-1.5 px-1.5 py-0.5 rounded-md text-[10px] font-medium ${
                          route.warning
                            ? 'bg-amber-100 text-amber-800 border border-amber-200'
                            : route.role === 'kitchen'
                              ? 'bg-orange-50 text-orange-700 border border-orange-100'
                              : route.role === 'bar'
                                ? 'bg-indigo-50 text-indigo-700 border border-indigo-100'
                                : 'bg-muted text-muted-foreground border border-border'
                        }`}
                        data-testid={`cart-item-route-${index}`}
                        title={route.warning ? 'No printer configured for this category — won\'t auto-print' : `Fires to: ${route.label}`}
                      >
                        {route.role === 'kitchen' ? <ChefHat size={10} /> : route.role === 'bar' ? <Coffee size={10} /> : <Printer size={10} />}
                        <span>{route.label}</span>
                      </div>
                    )}
                  </div>
                  <Button size="icon" variant="ghost" className="h-6 w-6 rounded-lg hover:bg-error/10" onClick={() => removeFromCart(index)}>
                    <Trash2 size={13} className="text-red-400" />
                  </Button>
                </div>
                <div className="flex items-center justify-between mt-2">
                  <div className="flex items-center gap-1.5">
                    <Button size="icon" variant="outline" className="h-10 w-10 rounded-lg border-border" onClick={() => updateQuantity(index, -1)}>
                      <Minus size={12} />
                    </Button>
                    <span className="font-bold text-sm w-7 text-center">{item.quantity}</span>
                    <Button size="icon" variant="outline" className="h-10 w-10 rounded-lg border-border" onClick={() => updateQuantity(index, 1)}>
                      <Plus size={12} />
                    </Button>
                  </div>
                  <span className="font-bold text-sm text-foreground">{currencySymbol} {grossOf(item.subtotal, item.price_includes_tax).toFixed(2)}</span>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* iter703 — Upsell strip: 1-tap suggestions powered by
          admin-curated rules. Renders nothing when cart is empty
          or no rules match — never blocks the cart UI. */}
      <POSUpsellStrip
        suggestions={upsellSuggestions}
        currencySymbol={currencySymbol}
        onAdd={(item) => {
          const full = menuItems.find((m) => m.id === item.id) || item;
          handleAddItem(full);
        }}
      />

      {/* Cart Footer - polished totals and buttons */}
      {cart.length > 0 && (
        <div className="border-t border-border p-4 space-y-3 bg-white">
          {/* Discount */}
          <div className="flex gap-2">
            <div className="flex-1 flex gap-1">
              <Input
                type="number"
                placeholder={t('pos_discount')}
                value={discount || ''}
                onChange={(e) => {
                  const v = Math.max(0, parseFloat(e.target.value) || 0);
                  setDiscount(discountType === 'percent' ? Math.min(100, v) : v);
                }}
                className="h-9 text-sm rounded-lg border-border"
              />
              <Select value={discountType} onValueChange={setDiscountType}>
                <SelectTrigger className="w-20 h-9 rounded-lg border-border">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="amount"><DollarSign size={14} /></SelectItem>
                  <SelectItem value="percent"><Percent size={14} /></SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Totals - cleaner spacing */}
          <div className="space-y-1.5 text-sm">
            <div className="flex justify-between text-muted-foreground">
              <span>{t('subtotal')} ({t('excl_vat')})</span>
              <span className="text-muted-foreground" data-testid="cart-subtotal">{currencySymbol} {subtotal.toFixed(2)}</span>
            </div>
            {discountAmount > 0 && (
              <div className="flex justify-between text-red-400">
                <span>{t('discount')}</span>
                <span>-{currencySymbol} {discountAmount.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between text-muted-foreground">
              <span>{t('vat_amount')} ({branchTax.tax_rate}%)</span>
              <span className="text-muted-foreground" data-testid="cart-vat">{currencySymbol} {tax.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-lg font-bold pt-2.5 border-t border-border">
              <span className="text-foreground">{t('grand_total')}</span>
              <span className="text-primary" data-testid="cart-total">{currencySymbol} {total.toFixed(2)}</span>
            </div>
          </div>

          {/* Payment Buttons - rounded, modern */}
          {editingOrder ? (
            <Button
              className="h-12 w-full bg-primary hover:bg-primary/90 text-white font-bold rounded-xl"
              onClick={onSaveEdit}
              disabled={cart.length === 0}
              data-testid="save-edit-btn"
            >
              <Save size={18} className="mr-2" />
              {t('pos_save_changes')} #{editingOrder.order_number}
            </Button>
          ) : (
            <>
            {onHoldBill && (
              <Button variant="outline" className="h-10 w-full mb-1.5 rounded-xl text-xs font-semibold"
                onClick={onHoldBill} disabled={cart.length === 0} data-testid="hold-bill-btn">
                Hold bill (phone / takeaway)
              </Button>
            )}
            <div className="grid grid-cols-3 gap-1.5">
              <Button
                variant={paymentMethod === 'cash' ? 'default' : 'outline'}
                className={`h-10 flex-col gap-0.5 rounded-xl ${paymentMethod === 'cash' ? 'bg-success hover:bg-success' : 'border-border text-muted-foreground'}`}
                onClick={() => { setPaymentMethod('cash'); setShowPayment(true); }}
                data-testid="pay-cash"
              >
                <Banknote size={15} />
                <span className="text-[9px] font-semibold">{t('pos_cash')}</span>
              </Button>
              <Button
                variant={paymentMethod === 'bank' ? 'default' : 'outline'}
                className={`h-10 flex-col gap-0.5 rounded-xl ${paymentMethod === 'bank' ? 'bg-info hover:bg-info' : 'border-border text-muted-foreground'}`}
                onClick={() => { setPaymentMethod('bank'); setShowPayment(true); }}
                data-testid="pay-bank"
              >
                <CreditCard size={15} />
                <span className="text-[9px] font-semibold">{t('pos_bank')}</span>
              </Button>
              <Button
                variant={paymentMethod === 'credit' ? 'default' : 'outline'}
                className={`h-10 flex-col gap-0.5 rounded-xl ${paymentMethod === 'credit' ? 'bg-warning hover:bg-warning' : 'border-border text-muted-foreground'}`}
                onClick={() => { setPaymentMethod('credit'); setShowCustomerSelect(true); }}
                data-testid="pay-credit"
              >
                <Users size={15} />
                <span className="text-[9px] font-semibold">{t('pos_credit')}</span>
              </Button>
            </div>
            </>
          )}

          {selectedCustomer && (
            <div className="bg-warning/10 rounded-lg p-2 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <User size={16} className="text-warning" />
                <span className="text-sm font-medium">{selectedCustomer.name}</span>
              </div>
              <Button size="sm" variant="ghost" onClick={() => setSelectedCustomer(null)}>
                <X size={14} />
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  </div>
  );
};

export default PosCart;
